"use client";

import React, { useState } from "react";
import { useToast } from "./Toast";

interface VideoScriptGeneratorProps {
  className?: string;
}

export default function VideoScriptGenerator({ className = "" }: VideoScriptGeneratorProps) {
  const [topic, setTopic] = useState("");
  const [duration, setDuration] = useState("5");
  const [style, setStyle] = useState("educativo");
  const [targetAudience, setTargetAudience] = useState("");
  const [script, setScript] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const { addToast } = useToast();

  const generateScript = async () => {
    if (!topic || !targetAudience) {
      addToast("Por favor, preencha todos os campos", "error");
      return;
    }

    setIsLoading(true);
    try {
      const response = await fetch("/api/generate-video-script", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ topic, duration, style, targetAudience }),
      });

      if (response.ok) {
        const data = await response.json();
        setScript(data.script);
        addToast("Script de vídeo gerado com sucesso!", "success");
      } else {
        const error = await response.json();
        addToast(error.error || "Erro ao gerar script", "error");
      }
    } catch (error) {
      console.error("Erro:", error);
      addToast("Erro ao gerar script", "error");
    } finally {
      setIsLoading(false);
    }
  };

  const copyToClipboard = () => {
    navigator.clipboard.writeText(script);
    addToast("Script copiado para a área de transferência!", "success");
  };

  return (
    <div className={`bg-white rounded-lg shadow-lg p-6 ${className}`}>
      <h3 className="text-2xl font-bold mb-6 text-gray-800">🎬 Gerador de Script de Vídeo</h3>
      
      <div className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Tópico do Vídeo
          </label>
          <input
            type="text"
            value={topic}
            onChange={(e) => setTopic(e.target.value)}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
            placeholder="Ex: Como aprender programação do zero"
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Duração (minutos)
            </label>
            <select
              value={duration}
              onChange={(e) => setDuration(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
            >
              <option value="1">1 minuto</option>
              <option value="3">3 minutos</option>
              <option value="5">5 minutos</option>
              <option value="10">10 minutos</option>
              <option value="15">15 minutos</option>
              <option value="30">30 minutos</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Estilo do Vídeo
            </label>
            <select
              value={style}
              onChange={(e) => setStyle(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
            >
              <option value="educativo">Educativo</option>
              <option value="entretenimento">Entretenimento</option>
              <option value="promocional">Promocional</option>
              <option value="tutorial">Tutorial</option>
              <option value="entrevista">Entrevista</option>
              <option value="documentário">Documentário</option>
            </select>
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Público-Alvo
          </label>
          <input
            type="text"
            value={targetAudience}
            onChange={(e) => setTargetAudience(e.target.value)}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
            placeholder="Ex: Iniciantes em programação, jovens adultos, etc."
          />
        </div>

        <button
          onClick={generateScript}
          disabled={isLoading || !topic || !targetAudience}
          className="w-full bg-primary text-white py-3 px-6 rounded-lg font-semibold hover:bg-primary/90 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors"
        >
          {isLoading ? "Gerando Script..." : "Gerar Script de Vídeo"}
        </button>

        {script && (
          <div className="mt-6 p-4 bg-gray-50 rounded-lg">
            <div className="flex justify-between items-center mb-3">
              <h4 className="font-semibold text-gray-800">Script Gerado:</h4>
              <button
                onClick={copyToClipboard}
                className="text-sm bg-secondary text-white px-3 py-1 rounded hover:bg-secondary/90 transition-colors"
              >
                Copiar
              </button>
            </div>
            <div className="whitespace-pre-wrap text-gray-700 max-h-96 overflow-y-auto">
              {script}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
